import { useCallback, useEffect, useRef, useState } from 'react';
import { blobToMp3 } from '@/lib/audio-mp3';

export type EstadoGravador = 'idle' | 'gravando' | 'convertendo' | 'pronto';

export interface Gravador {
  estado: EstadoGravador;
  tempo: number;
  mp3Blob: Blob | null;
  previewUrl: string | null;
  erro: string | null;
  suportado: boolean;
  iniciar: () => Promise<void>;
  parar: () => void;
  cancelar: () => void;
}

export function useGravadorAudio(): Gravador {
  const [estado, setEstado] = useState<EstadoGravador>('idle');
  const [tempo, setTempo] = useState(0);
  const [mp3Blob, setMp3Blob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const urlRef = useRef<string | null>(null);

  const suportado =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';

  const pararTimer = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const pararStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const limparUrl = () => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const cancelar = useCallback(() => {
    pararTimer();
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null;
      rec.stop();
    }
    recorderRef.current = null;
    pararStream();
    chunksRef.current = [];
    limparUrl();
    setMp3Blob(null);
    setTempo(0);
    setErro(null);
    setEstado('idle');
  }, []);

  const iniciar = useCallback(async () => {
    setErro(null);
    if (!suportado) {
      setErro('gravacao-nao-suportada');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        pararStream();
        const bruto = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setEstado('convertendo');
        try {
          const mp3 = await blobToMp3(bruto);
          const url = URL.createObjectURL(mp3);
          urlRef.current = url;
          setMp3Blob(mp3);
          setPreviewUrl(url);
          setEstado('pronto');
        } catch {
          setErro('conversao-falhou');
          setEstado('idle');
        }
      };
      rec.start();
      setTempo(0);
      setEstado('gravando');
      timerRef.current = window.setInterval(() => setTempo((s) => s + 1), 1000);
    } catch {
      setErro('permissao-negada');
      pararStream();
      setEstado('idle');
    }
  }, [suportado]);

  const parar = useCallback(() => {
    pararTimer();
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop(); // dispara onstop → conversão
  }, []);

  useEffect(() => () => {
    pararTimer();
    pararStream();
    limparUrl();
  }, []);

  return { estado, tempo, mp3Blob, previewUrl, erro, suportado, iniciar, parar, cancelar };
}
